import axios from 'axios';
import { PublicClientApplication } from '@azure/msal-browser';
import { msalConfig } from '../config/msalConfig';

// Create MSAL instance for token acquisition
const msalInstance = new PublicClientApplication(msalConfig);

// Initialize MSAL
msalInstance.initialize().then(() => {
  console.log('MSAL initialized for API service');
  console.log('MSAL Config:', {
    clientId: msalConfig.auth.clientId,
    authority: msalConfig.auth.authority,
    redirectUri: msalConfig.auth.redirectUri
  });
}).catch((error) => {
  console.error('MSAL initialization failed in API service:', error);
});

// Create axios instance with base configuration
const api = axios.create({
  baseURL: '',
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Function to get access token silently
const getAccessToken = async () => {
  try {
    console.log('Attempting to acquire token silently...');
    const accounts = msalInstance.getAllAccounts();
    console.log('Available accounts:', accounts.length);
    
    if (accounts.length === 0) {
      throw new Error('No accounts found - user needs to login first');
    }

    const account = accounts[0];
    console.log('Using account:', account.username);
    
    const tokenRequest = {
      scopes: ['api://33e61b30-56ae-40ac-b2f6-1f40ee998d3a/.default'],
      account: account,
    };

    console.log('Requesting token with scopes:', tokenRequest.scopes);
    const response = await msalInstance.acquireTokenSilent(tokenRequest);
    console.log('Token acquired successfully');
    return response.accessToken;
  } catch (error) {
    console.error('Error acquiring token silently:', error);
    console.error('Error details:', {
      errorCode: error.errorCode,
      errorMessage: error.errorMessage,
      subError: error.subError
    });
    throw error;
  }
};

// Add request interceptor to include MSAL access token
api.interceptors.request.use(
  async (config) => {
    // Only add token for secure endpoints
    if (config.url && config.url.includes('/secure/')) {
      console.log(`Adding token to secure request: ${config.method?.toUpperCase()} ${config.url}`);
      try {
        const token = await getAccessToken();
        config.headers.Authorization = `Bearer ${token}`;
        console.log('Token added to request headers');
      } catch (error) {
        console.error('Failed to get access token for secure request:', error);
        console.warn('Proceeding without access token - server will return 401');
        // Don't throw error here to prevent infinite loop
        // Let the request proceed without token and let the server return 401
      }
    }
    return config;
  },
  (error) => {
    console.error('Request interceptor error:', error);
    return Promise.reject(error);
  }
);

// Add response interceptor for error handling
api.interceptors.response.use(
  (response) => {
    console.log(`API Response: ${response.config.method?.toUpperCase()} ${response.config.url} - ${response.status}`);
    return response;
  },
  (error) => {
    if (error.response?.status === 401) {
      console.error('Unauthorized access (401) - API requires valid access token');
      console.error('Response data:', error.response?.data);
      console.error('Request URL:', error.config?.url);
      console.error('Request method:', error.config?.method);
      
      // Check if this is a token acquisition issue
      if (error.config?.url?.includes('/secure/')) {
        console.error('This is a secure endpoint - token acquisition may have failed');
        console.error('Please check if user is logged in and has proper permissions');
      }
    } else if (error.response?.status === 403) {
      console.error('Forbidden access (403) - User may not have required permissions');
    } else {
      console.error('API Error:', error.response?.status, error.message);
    }
    return Promise.reject(error);
  }
);

// Tournament API endpoints
export const tournamentAPI = {
  // Get all tournaments (public endpoint)
  getAll: () => api.get('/public/api/v1/tournaments'),
  
  // Create tournament (secure endpoint)
  create: (tournamentData) => api.post('/secure/api/v1/tournaments', tournamentData),
  
  // Update tournament (secure endpoint)
  update: (tournamentId, tournamentData) => api.put(`/secure/api/v1/tournaments/${tournamentId}`, tournamentData),
  
  // Delete tournament (secure endpoint)
  delete: (tournamentId) => api.delete(`/secure/api/v1/tournaments/${tournamentId}`),
};

// Player API endpoints
export const playerAPI = {
  // Get all players (secure endpoint for admin)
  getAll: () => api.get('/secure/api/v1/players'),
  
  // Create player registration (public endpoint)
  create: (playerData) => api.post('/public/api/v1/players', playerData),
  
  // Update player (secure endpoint)
  update: (playerId, playerData) => api.put(`/secure/api/v1/players/${playerId}`, playerData),
  
  // Delete player (secure endpoint)
  delete: (playerId) => api.delete(`/secure/api/v1/players/${playerId}`),
};

// Tournament Registration API endpoints
export const registrationAPI = {
  // Get all tournament registrations (public endpoint)
  getAll: () => api.get('/public/api/v1/tournament-registrations'),
  
  // Get registrations for a specific tournament
  getByTournament: (tournamentId) => api.get(`/public/api/v1/tournament-registrations/tournament/${tournamentId}`),
};

export default api;
